// src/components/ChatWidget.jsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion'; 

// --- Icon Components ---

const ChatBubbleIcon = ({ className }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
    </svg>
);

const CloseIcon = ({ className }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
);

const SendIcon = ({ className }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="22" y1="2" x2="11" y2="13"></line>
        <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
    </svg>
);

// --- Component to render individual messages ---
const ChatMessage = ({ message }) => {
    // Determine if the message is from the current user (us) or the community (them)
    const isUser = message.sender === 'user';
    const align = isUser ? 'flex-end' : 'flex-start';
    
    // UPDATED STYLES: Community message background is white, user message is light blue
    const bgColor = isUser ? 'var(--color-sky-light)' : 'white';
    const color = 'var(--color-navy)'; 

    // Framer Motion variants for individual message entry
    const messageVariants = {
        initial: { opacity: 0, y: 50, scale: 0.8 },
        animate: { opacity: 1, y: 0, scale: 1, transition: { type: "spring", stiffness: 300, damping: 25 } },
        exit: { opacity: 0, x: isUser ? 50 : -50, transition: { duration: 0.2 } }
    };

    return (
        <motion.div 
            className="chat-message-container" 
            style={{ display: 'flex', justifyContent: align }}
            variants={messageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            layout // Enable layout animation for smooth scrolling/reordering
        >
            <div 
                className="chat-message-bubble" 
                style={{ 
                    backgroundColor: bgColor, 
                    color: color, 
                    // Add a border to non-user messages for visibility against a white background
                    border: isUser ? 'none' : '1px solid var(--color-gray-light)' 
                }} 
            >
                <p style={{ margin: 0 }}>{message.text}</p>
            </div>
        </motion.div>
    );
};


const ChatWidget = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        { id: 1, text: "Welcome to the NYC Local Hub! Ask a question or share a local update.", sender: 'community' },
    ]);
    const [inputMessage, setInputMessage] = useState('');
    const unreadCount = 3; 

    const handleToggle = () => {
        setIsOpen(!isOpen);
    };

    const handleSendMessage = (e) => {
        e.preventDefault();
        if (inputMessage.trim() === '') return;

        const newMessage = {
            id: Date.now(),
            text: inputMessage,
            sender: 'user', 
        };

        setMessages((prevMessages) => [...prevMessages, newMessage]);
        setInputMessage('');

        // Simulate a community response after a short delay
        setTimeout(() => {
            setMessages((prevMessages) => [
                ...prevMessages, 
                { id: Date.now() + 1, text: "Got it! Your message has been shared with local members.", sender: 'community' }
            ]);
        }, 1500);
    };
    
    // Animation variants for the chat window
    const windowVariants = {
        hidden: { opacity: 0, scale: 0.9, y: 50 },
        visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.3 } }
    };

    return (
        <div className="chat-widget-container">
            
            <AnimatePresence>
                {isOpen && (
                    <motion.div 
                        className="chat-window"
                        variants={windowVariants}
                        initial="hidden"
                        animate="visible"
                        exit="hidden" 
                    >
                        
                        <div className="chat-header">
                            <div className="chat-header-info">
                                 <p className="chat-title">NYC Local Hub</p>
                                 <small className="chat-subtitle">Verified Users Only</small>
                            </div>
                            <button onClick={handleToggle} className="chat-close-button" aria-label="Close Chat">
                                <CloseIcon className="chat-close-icon" />
                            </button>
                        </div>

                        {/* Message Content Area */}
                        <div className="chat-content">
                            <AnimatePresence initial={false}>
                                {messages.map((msg) => (
                                    <ChatMessage key={msg.id} message={msg} />
                                ))}
                            </AnimatePresence>
                        </div>
                        
                        {/* Input Area (Form handles sending on Enter/click) */}
                        <form className="chat-input-area-modern" onSubmit={handleSendMessage}>
                            <input 
                                type="text" 
                                placeholder="Type a message..." 
                                className="chat-input-field"
                                value={inputMessage}
                                onChange={(e) => setInputMessage(e.target.value)}
                            />
                            <motion.button 
                                type="submit" 
                                className="chat-send-button" 
                                aria-label="Send Message"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.9 }}
                                disabled={inputMessage.trim() === ''} 
                            >
                                <SendIcon className="chat-send-icon" />
                            </motion.button>
                        </form>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Floating Action Button (FAB) */}
            <motion.button 
                className={`chat-fab-button ${isOpen ? 'chat-fab-open' : ''}`}
                onClick={handleToggle}
                aria-label={isOpen ? "Close Chat" : "Open Chat"}
                whileHover={{ scale: 1.1, rotate: isOpen ? 0 : 90 }} 
                whileTap={{ scale: 0.9 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
                {!isOpen && unreadCount > 0 && <span className="chat-badge">{unreadCount}</span>}
                
                {isOpen ? (
                    <CloseIcon className="chat-fab-icon" />
                ) : (
                    <ChatBubbleIcon className="chat-fab-icon" />
                )}
            </motion.button>
        </div>
    );
};

export default ChatWidget;